// src/pages/FondationsLanding.jsx
// Page publique /fondations : offre ARTICHAUT pour les fondations d'entreprise et les fondations de particuliers,
// et pour les Art Advisors qui les accompagnent. Même gabarit que /galeries et /notaires.
// Textes en français, anglais et allemand : voir src/lib/fondationsText.js (langue de l'interface).
// Formulaire → fonction contact-lead (table professional_leads, accusé de réception, notification équipe).
// Règles : ne rien promettre qui n'existe pas ; aucun conseil fiscal ou juridique ; aucune question sur la valeur
// de la collection au premier contact ; aucune promesse d'intégration.
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { track, getAnonId } from "@/lib/track";
import { BRAND_NAME } from "@/config/brand";
import BrandMark from "@/components/BrandMark";
import { useFondationsText } from "../lib/fondationsText";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Libellés du message transmis à l'équipe : toujours en français, quelle que soit la langue du visiteur.
const LEAD_NEEDS = { corporate: "Fondation d'entreprise", family: "Fondation de particulier", advisor: "Art Advisor d'une fondation", other: "Autre" };

const EMPTY = {
  foundation: "", contact: "", role: "", email: "", phone: "", need: "corporate", volume: "", deadline: "",
  callback: false, consent: false, website: "",
};

const input = "px-4 py-3 border border-[#e5e0d8] rounded-lg text-sm bg-[#f9f7f4] w-full";

export default function FondationsLanding() {
  const { tx, lang } = useFondationsText();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const contactRef = useRef(null);

  useEffect(() => {
    document.title = tx.docTitle(BRAND_NAME);
    track("fondations_page_view", { meta: { lang, source: new URLSearchParams(window.location.search).get("utm_source") || document.referrer || null } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const goContact = (need) => {
    if (need) setForm((f) => ({ ...f, need }));
    requestAnimationFrame(() => contactRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const field = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.foundation.trim()) { setError(tx.err.foundation); return; }
    if (!form.contact.trim()) { setError(tx.err.contact); return; }
    if (!EMAIL_RE.test(form.email.trim())) { setError(tx.err.email); return; }
    if (!form.consent) { setError(tx.err.consent); return; }
    setSending(true); setError(null);
    try {
      const [firstName, ...rest] = form.contact.trim().split(/\s+/);
      // Le préfixe [Fondations] permet de retrouver ces demandes dans professional_leads (source « contact-general »).
      const message = [
        "[Fondations]",
        `Profil : ${LEAD_NEEDS[form.need] || form.need}`,
        `Langue de la page : ${lang}`,
        form.role.trim() && `Fonction : ${form.role.trim()}`,
        form.volume.trim() && `Volume approximatif : ${form.volume.trim()}`,
        form.deadline.trim() && `Échéance : ${form.deadline.trim()}`,
        `Rappel souhaité : ${form.callback ? "oui" : "non"}`,
      ].filter(Boolean).join("\n");

      const { data, error: err } = await supabase.functions.invoke("contact-lead", {
        body: {
          first_name: firstName || "",
          last_name: rest.join(" "),
          company: form.foundation.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          client_count: form.volume.trim(),
          message,
          website: form.website, // champ piège : doit rester vide
          brand: BRAND_NAME,
          anon_id: getAnonId(),
        },
      });
      if (err || !(data && data.ok)) throw err || new Error("lead_failed");
      track("fondations_lead", { meta: { need: form.need, callback: form.callback, lang } });
      setDone(true);
    } catch (err) {
      console.error("[fondations] lead:", err);
      setError(tx.err.generic);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f7f4] text-[#1a1a1a]" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <header className="sticky top-0 z-30 bg-[#f9f7f4]/95 backdrop-blur border-b border-[#e5e0d8]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" aria-label={BRAND_NAME} className="no-underline text-inherit"><BrandMark className="text-2xl tracking-[0.15em]" /></Link>
          <div className="flex items-center gap-4">
            <a href="#profils" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">{tx.nav.profiles}</a>
            <a href="#advisors" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">{tx.nav.advisors}</a>
            <a href="#confidentialite" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">{tx.nav.privacy}</a>
            <button onClick={() => goContact()} className="text-sm px-4 py-2 bg-[#1a1a1a] text-[#f9f7f4] rounded-md hover:bg-black">
              {tx.nav.cta}
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-12 md:pt-16 pb-14">
        <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-5">{tx.eyebrow}</div>
        <h1 className="text-4xl md:text-5xl font-bold leading-[1.1] max-w-3xl">{tx.h1}</h1>
        <p className="mt-6 text-lg text-[#4a453d] leading-relaxed max-w-2xl">{tx.lead(BRAND_NAME)}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={() => goContact()} className="px-6 py-3 bg-[#1a1a1a] text-[#f9f7f4] rounded-md text-base hover:bg-black">
            {tx.ctaMain}
          </button>
          <a href="#profils" className="px-6 py-3 border border-[#1a1a1a] rounded-md text-base hover:bg-[#1a1a1a] hover:text-[#f9f7f4] transition-colors">
            {tx.ctaProfiles}
          </a>
        </div>
      </section>

      <section id="profils" className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold mb-8">{tx.profilesTitle}</h2>
        <div className="grid md:grid-cols-2 gap-5">
          {tx.profiles.map((p) => (
            <div key={p.id} className="bg-white border border-[#e5e0d8] rounded-2xl p-6 flex flex-col">
              <div className="text-xs tracking-[0.16em] uppercase text-[#a9843f] mb-3">{p.n}</div>
              <h3 className="text-xl font-bold mb-4 leading-snug">{p.t}</h3>
              <ul className="grid gap-3 text-sm text-[#4a453d] leading-relaxed list-disc pl-5 mb-6">
                {p.items.map((it) => <li key={it}>{it}</li>)}
              </ul>
              <button onClick={() => goContact(p.id)} className="mt-auto self-start px-4 py-2 border border-[#1a1a1a] rounded-md text-sm hover:bg-[#1a1a1a] hover:text-[#f9f7f4] transition-colors">
                {p.button}
              </button>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm italic text-[#6b6459] max-w-3xl">{tx.disclaimer(BRAND_NAME)}</p>
      </section>

      <section id="advisors" className="bg-white border-y border-[#e5e0d8]">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold mb-4">{tx.advisorTitle}</h2>
          <p className="text-lg text-[#4a453d] leading-relaxed mb-6">{tx.advisorLead(BRAND_NAME)}</p>
          <ul className="grid gap-3">
            {tx.advisor.map((a) => (
              <li key={a} className="bg-[#f9f7f4] border border-[#e5e0d8] rounded-xl px-5 py-4 text-base text-[#2a261f]">{a}</li>
            ))}
          </ul>
          <button onClick={() => goContact("advisor")} className="mt-6 px-5 py-2.5 bg-[#1a1a1a] text-[#f9f7f4] rounded-md text-sm hover:bg-black">
            {tx.advisorBtn}
          </button>
        </div>
      </section>

      <section id="methode" className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold mb-8">{tx.stepsTitle}</h2>
        <ol className="grid gap-3">
          {tx.steps.map((s, i) => (
            <li key={s} className="flex items-start gap-4 bg-white border border-[#e5e0d8] rounded-xl px-5 py-4">
              <span className="shrink-0 w-8 h-8 rounded-full bg-[#1a1a1a] text-[#c9a961] flex items-center justify-center text-sm font-bold font-sans">{i + 1}</span>
              <span className="text-base text-[#2a261f] pt-0.5">{s}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="bg-white border-y border-[#e5e0d8]">
        <div className="max-w-4xl mx-auto px-6 py-14">
          <h2 className="text-3xl font-bold mb-4">{tx.galTitle}</h2>
          <p className="text-lg text-[#4a453d] leading-relaxed">{tx.galBody(BRAND_NAME)}</p>
        </div>
      </section>

      <section id="confidentialite" className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold mb-8">{tx.privacyTitle}</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {tx.privacy(BRAND_NAME).map(([t, d]) => (
            <div key={t} className="bg-white border border-[#e5e0d8] rounded-xl p-5">
              <h3 className="font-bold text-[#2a261f] mb-1">{t}</h3>
              <p className="text-sm text-[#4a453d] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section ref={contactRef} id="contact" className="max-w-3xl mx-auto px-6 pb-16 pt-4">
        <h2 className="text-3xl font-bold mb-2">{tx.formTitle}</h2>
        <p className="text-[#4a453d] mb-8">{tx.formLead}</p>

        {done ? (
          <div className="bg-[#eef5f1] border border-[#cfe3d8] rounded-xl p-8 text-center">
            <div className="text-2xl mb-2">✓</div>
            <h3 className="text-lg font-bold mb-1">{tx.doneTitle}</h3>
            <p className="text-sm text-[#4a453d]">{tx.doneBody}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[#e5e0d8] rounded-2xl p-6 md:p-8 grid gap-4">
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={field("website")}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
            <input value={form.foundation} onChange={field("foundation")} placeholder={tx.ph.foundation} className={input} />
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.contact} onChange={field("contact")} placeholder={tx.ph.contact} className={input} />
              <input value={form.role} onChange={field("role")} placeholder={tx.ph.role} className={input} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input type="email" value={form.email} onChange={field("email")} placeholder={tx.ph.email} className={input} />
              <input type="tel" value={form.phone} onChange={field("phone")} placeholder={tx.ph.phone} className={input} />
            </div>
            <div>
              <label className="block text-xs text-[#8a8378] mb-2">{tx.situation}</label>
              <div className="flex flex-wrap gap-2">
                {tx.needs.map(([v, l]) => (
                  <button type="button" key={v} onClick={() => setForm((f) => ({ ...f, need: v }))}
                    className={`px-4 py-2 rounded-lg text-sm border transition-colors ${form.need === v ? "bg-[#1a1a1a] text-[#f9f7f4] border-[#1a1a1a]" : "border-[#e5e0d8] text-[#4a453d] hover:bg-[#f9f7f4]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.volume} onChange={field("volume")} placeholder={tx.ph.volume} className={input} />
              <input value={form.deadline} onChange={field("deadline")} placeholder={tx.ph.deadline} className={input} />
            </div>
            <label className="flex items-start gap-3 text-sm text-[#4a453d]">
              <input type="checkbox" checked={form.callback} onChange={(e) => setForm((f) => ({ ...f, callback: e.target.checked }))} className="mt-1" />
              <span>{tx.callback}</span>
            </label>
            <label className="flex items-start gap-3 text-xs text-[#6b6459]">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-0.5" />
              <span>{tx.consentPre}{" "}
                <Link to="/privacy-policy" className="underline">{tx.privacyLink}</Link>.</span>
            </label>
            {error && <p className="text-sm text-[#dc2626]">{error}</p>}
            <button type="submit" disabled={sending} className="px-6 py-3 bg-[#c9a961] text-[#1a1a1a] font-semibold rounded-lg hover:bg-[#b9975a] disabled:opacity-60">
              {sending ? tx.sending : tx.submit}
            </button>
            <p className="text-xs text-[#8a8378]">{tx.formNote}</p>
          </form>
        )}
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-xs text-[#8a8378] flex flex-wrap gap-4 justify-between">
        <span>© {new Date().getFullYear()} {BRAND_NAME} — par ARTKIV</span>
        <span className="flex gap-4">
          <Link to="/mentions-legales" className="hover:text-black">{tx.legal}</Link>
          <Link to="/privacy-policy" className="hover:text-black">{tx.conf}</Link>
        </span>
      </footer>
    </div>
  );
}
